import { useState, useEffect } from "react";
import type { User } from "@supabase/supabase-js";
import type { Tenant } from "@/lib/types";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth-context";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { User as UserIcon, MapPin, Save, LogOut, Loader2, ShieldCheck, Eye, EyeOff, Upload, FileImage } from "lucide-react";
import UserMarketingPreference from "./UserMarketingPreference";

interface Props {
  tenant: Tenant;
  user: User;
}

interface ProfileData {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  dob: string;
  ssn_last4: string;
  licence_front_path: string;
  licence_back_path: string;
  postal_address: {
    line1: string;
    line2: string;
    city: string;
    state: string;
    postalCode: string;
    country: string;
  };
}

const EMPTY_ADDRESS = { line1: "", line2: "", city: "", state: "", postalCode: "", country: "United States" };

const UserProfile = ({ tenant, user }: Props) => {
  const { signOut } = useAuth();
  const { toast } = useToast();
  const [profile, setProfile] = useState<ProfileData>({
    first_name: "",
    last_name: "",
    email: user.email || "",
    phone: "",
    dob: "",
    ssn_last4: "",
    licence_front_path: "",
    licence_back_path: "",
    postal_address: { ...EMPTY_ADDRESS },
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  // Visibility toggles for sensitive fields
  const [showDob, setShowDob] = useState(false);
  const [showSsn, setShowSsn] = useState(false);
  const [showLicence, setShowLicence] = useState(false);

  useEffect(() => {
    const fetchProfile = async () => {
      setLoading(true);
      
      const [profileRes, waitlistRes, cardRes] = await Promise.all([
        supabase
          .from("profiles")
          .select("first_name, last_name, email, phone, dob, postal_address, ssn_last4, licence_front_path, licence_back_path")
          .eq("user_id", user.id)
          .maybeSingle(),
        supabase
          .from("waitlist_entries")
          .select("first_name, last_name")
          .eq("user_id", user.id)
          .order("created_at", { ascending: false })
          .limit(1)
          .maybeSingle(),
        supabase
          .from("card_requests")
          .select("first_name, last_name, postal_address")
          .eq("user_id", user.id)
          .order("created_at", { ascending: false })
          .limit(1)
          .maybeSingle(),
      ]);

      const data = profileRes.data;
      const waitlist = waitlistRes.data;
      const card = cardRes.data;

      if (data) {
        const fallbackFirstName = waitlist?.first_name || card?.first_name || "";
        const fallbackLastName = waitlist?.last_name || card?.last_name || "";
        const addr = (data.postal_address as any) || (card?.postal_address as any) || {};
        setProfile({
          first_name: data.first_name || fallbackFirstName,
          last_name: data.last_name || fallbackLastName,
          email: data.email || user.email || "",
          phone: (data as any).phone || "",
          dob: (data as any).dob || "",
          ssn_last4: (data as any).ssn_last4 || "",
          licence_front_path: (data as any).licence_front_path || "",
          licence_back_path: (data as any).licence_back_path || "",
          postal_address: {
            line1: addr.line1 || "",
            line2: addr.line2 || "",
            city: addr.city || "",
            // `region`/`postcode` are what the consumer flow WRITES; `state`/
            // `postalCode` are legacy keys. Read both or State/ZIP look unsaved.
            state: addr.region || addr.state || "",
            postalCode: addr.postcode || addr.postalCode || "",
            country: addr.country || "",
          },
        });

        if (!data.first_name && fallbackFirstName) {
          supabase.from("profiles").update({
            first_name: fallbackFirstName,
            last_name: fallbackLastName,
          }).eq("user_id", user.id).then(() => {});
        }
      }
      setLoading(false);
    };
    fetchProfile();
  }, [user.id]);

  const update = (field: keyof ProfileData, value: any) => {
    setProfile((p) => ({ ...p, [field]: value }));
    setDirty(true);
  };

  const updateAddress = (field: string, value: string) => {
    setProfile((p) => ({
      ...p,
      postal_address: { ...p.postal_address, [field]: value },
    }));
    setDirty(true);
  };

  const handleSave = async () => {
    setSaving(true);
    const { error } = await supabase
      .from("profiles")
      .update({
        first_name: profile.first_name || null,
        last_name: profile.last_name || null,
        phone: profile.phone || null,
        dob: profile.dob || null,
        ssn_last4: profile.ssn_last4 || null,
        // Write the CANONICAL keys so this page can never re-introduce the
        // legacy `state`/`postalCode` split the consumer flow moved off.
        postal_address: {
          line1: profile.postal_address.line1,
          line2: profile.postal_address.line2,
          city: profile.postal_address.city,
          region: profile.postal_address.state,
          postcode: profile.postal_address.postalCode,
          country: profile.postal_address.country,
        } as any,
      } as any)
      .eq("user_id", user.id);

    if (error) {
      toast({ title: "Save failed", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "Profile saved ✓" });
      setDirty(false);
    }
    setSaving(false);
  };

  const handleLicenceUpload = async (side: "front" | "back", file: File) => {
    const path = `${user.id}/${side}-${Date.now()}.${file.name.split(".").pop()}`;
    const { error: uploadErr } = await supabase.storage
      .from("kyc-documents")
      .upload(path, file, { upsert: true });

    if (uploadErr) {
      toast({ title: "Upload failed", description: uploadErr.message, variant: "destructive" });
      return;
    }

    const field = side === "front" ? "licence_front_path" : "licence_back_path";
    await supabase.from("profiles").update({ [field]: path } as any).eq("user_id", user.id);
    update(field as keyof ProfileData, path);
    toast({ title: `${side === "front" ? "Front" : "Back"} uploaded ✓` });
  };

  // Mask helpers
  const maskDob = (dob: string) => {
    if (!dob) return "";
    return "••/••/••••";
  };

  const maskSsn = (ssn: string) => {
    if (!ssn) return "";
    return `•••-••-${ssn}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4 max-w-lg mx-auto animate-fade-in">
      <h2 className="text-xl font-bold font-display">Profile</h2>

      {/* Personal Info */}
      <Card className="shadow-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <UserIcon className="h-4 w-4" /> Personal Information
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">First Name</Label>
              <Input value={profile.first_name} onChange={(e) => update("first_name", e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Last Name</Label>
              <Input value={profile.last_name} onChange={(e) => update("last_name", e.target.value)} />
            </div>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Email</Label>
            <Input value={profile.email} disabled className="bg-muted/50 text-sm text-left" />
            <p className="text-xs text-muted-foreground">Email cannot be changed here</p>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Phone</Label>
            <Input value={profile.phone} onChange={(e) => update("phone", e.target.value)} placeholder="+1 (555) 123-4567" />
          </div>
        </CardContent>
      </Card>

      {/* Marketing consent — editable both ways, append-only ledger (B8) */}
      <UserMarketingPreference />



      {/* Identity Verification */}
      <Card className="shadow-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <ShieldCheck className="h-4 w-4" /> Identity Verification
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {/* Date of Birth */}
          <div className="space-y-1">
            <Label className="text-xs flex items-center justify-between">
              Date of Birth
              <button type="button" onClick={() => setShowDob(!showDob)} className="text-muted-foreground hover:text-foreground transition-colors">
                {showDob ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
              </button>
            </Label>
            {showDob ? (
              <Input type="date" value={profile.dob} onChange={(e) => update("dob", e.target.value)} />
            ) : (
              <Input
                value={profile.dob ? maskDob(profile.dob) : ""}
                readOnly
                onClick={() => setShowDob(true)}
                placeholder="••/••/••••"
                className="cursor-pointer"
              />
            )}
          </div>

          {/* SSN */}
          <div className="space-y-1">
            <Label className="text-xs flex items-center justify-between">
              SSN (Last 4 digits)
              <button type="button" onClick={() => setShowSsn(!showSsn)} className="text-muted-foreground hover:text-foreground transition-colors">
                {showSsn ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
              </button>
            </Label>
            <Input
              type={showSsn ? "text" : "password"}
              value={profile.ssn_last4}
              onChange={(e) => {
                const val = e.target.value.replace(/\D/g, "").slice(0, 4);
                update("ssn_last4", val);
              }}
              placeholder={showSsn ? "1234" : "••••"}
              maxLength={4}
              inputMode="numeric"
            />
            <p className="text-xs text-muted-foreground">Required for card verification</p>
          </div>

          {/* Driving Licence */}
          <div className="space-y-2">
            <Label className="text-xs flex items-center justify-between">
              Driving Licence
              <button type="button" onClick={() => setShowLicence(!showLicence)} className="text-muted-foreground hover:text-foreground transition-colors">
                {showLicence ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
              </button>
            </Label>

            {showLicence ? (
              <div className="grid grid-cols-2 gap-3">
                {/* Front */}
                <div className="space-y-1.5">
                  <p className="text-xs text-muted-foreground">Front</p>
                  {profile.licence_front_path ? (
                    <div className="relative rounded-lg border bg-muted/30 p-3 text-center">
                      <FileImage className="h-6 w-6 mx-auto text-success mb-1" />
                      <p className="text-xs text-success font-medium">Uploaded</p>
                      <label className="absolute inset-0 cursor-pointer">
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => e.target.files?.[0] && handleLicenceUpload("front", e.target.files[0])}
                        />
                      </label>
                    </div>
                  ) : (
                    <label className="flex flex-col items-center gap-1.5 rounded-lg border border-dashed p-3 cursor-pointer hover:bg-muted/50 transition-colors">
                      <Upload className="h-5 w-5 text-muted-foreground" />
                      <span className="text-xs text-muted-foreground">Upload</span>
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => e.target.files?.[0] && handleLicenceUpload("front", e.target.files[0])}
                      />
                    </label>
                  )}
                </div>
                {/* Back */}
                <div className="space-y-1.5">
                  <p className="text-xs text-muted-foreground">Back</p>
                  {profile.licence_back_path ? (
                    <div className="relative rounded-lg border bg-muted/30 p-3 text-center">
                      <FileImage className="h-6 w-6 mx-auto text-success mb-1" />
                      <p className="text-xs text-success font-medium">Uploaded</p>
                      <label className="absolute inset-0 cursor-pointer">
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => e.target.files?.[0] && handleLicenceUpload("back", e.target.files[0])}
                        />
                      </label>
                    </div>
                  ) : (
                    <label className="flex flex-col items-center gap-1.5 rounded-lg border border-dashed p-3 cursor-pointer hover:bg-muted/50 transition-colors">
                      <Upload className="h-5 w-5 text-muted-foreground" />
                      <span className="text-xs text-muted-foreground">Upload</span>
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => e.target.files?.[0] && handleLicenceUpload("back", e.target.files[0])}
                      />
                    </label>
                  )}
                </div>
              </div>
            ) : (
              <div className="rounded-lg border bg-muted/20 p-3 flex items-center gap-2">
                <ShieldCheck className="h-4 w-4 text-muted-foreground shrink-0" />
                <p className="text-xs text-muted-foreground">
                  {profile.licence_front_path && profile.licence_back_path
                    ? "Documents uploaded — tap the eye icon to view or replace"
                    : profile.licence_front_path || profile.licence_back_path
                      ? "Partially uploaded — tap the eye icon to complete"
                      : "Tap the eye icon to upload your driving licence"}
                </p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Delivery Address */}
      <Card className="shadow-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <MapPin className="h-4 w-4" /> Delivery Address
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="space-y-1">
            <Label className="text-xs">Address Line 1</Label>
            <Input value={profile.postal_address.line1} onChange={(e) => updateAddress("line1", e.target.value)} placeholder="123 Main Street" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Address Line 2</Label>
            <Input value={profile.postal_address.line2} onChange={(e) => updateAddress("line2", e.target.value)} placeholder="Apt, Suite" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">City</Label>
              <Input value={profile.postal_address.city} onChange={(e) => updateAddress("city", e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">State</Label>
              <Input value={profile.postal_address.state} onChange={(e) => updateAddress("state", e.target.value)} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Postal Code</Label>
              <Input value={profile.postal_address.postalCode} onChange={(e) => updateAddress("postalCode", e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Country</Label>
              <Input value={profile.postal_address.country} onChange={(e) => updateAddress("country", e.target.value)} />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Save button */}
      <Button
        className="w-full gap-2 font-semibold"
        onClick={handleSave}
        disabled={!dirty || saving}
        style={dirty ? { backgroundColor: `hsl(${tenant.primaryColor})` } : undefined}
      >
        {saving ? <><Loader2 className="h-4 w-4 animate-spin" /> Saving...</> : <><Save className="h-4 w-4" /> Save Changes</>}
      </Button>

      {/* Sign Out */}
      <Button variant="outline" className="w-full gap-2 text-muted-foreground" onClick={signOut}>
        <LogOut className="h-4 w-4" /> Sign Out
      </Button>
    </div>
  );
};

export default UserProfile;
